import { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, IconButton, Input, Modal, ModalClose, ModalDialog,
  Option, Select, Stack, Switch, Table, Textarea, Typography, FormControl, FormLabel,
} from "@mui/joy";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

import { useAdmin } from "../../AdminContext";
import {
  APIAdminTraitsList, APIAdminTraitCreate, APIAdminTraitUpdate, APIAdminTraitDelete,
} from "../../api_admin";

const EMPTY = {
  code: "", name: "", description: "",
  value_type: "scale", match_strategy: "similar",
  scale_min: 0, scale_max: 10, weight: 1,
  is_filterable: false, is_public: false, is_active: true,
};

const VALUE_TYPES = [["scale", "Числовая шкала"], ["categorical", "Категория"]];
const STRATEGIES = [["similar", "Близость (чем ближе — лучше)"], ["equal", "Точное совпадение"], ["none", "Не в подборе"]];

export default function AdminTraitsPage() {
  const { adminToken, canEdit, isSuperadmin } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await APIAdminTraitsList(adminToken);
      setItems(data.items || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { if (adminToken) load(); /* eslint-disable-next-line */ }, [adminToken]);

  const openCreate = () => { setEditId(null); setDraft(EMPTY); setError(null); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setDraft({
      code: t.code, name: t.name, description: t.description || "",
      value_type: t.value_type, match_strategy: t.match_strategy,
      scale_min: t.scale_min, scale_max: t.scale_max, weight: t.weight,
      is_filterable: t.is_filterable, is_public: t.is_public, is_active: t.is_active,
    });
    setError(null);
    setOpen(true);
  };

  const save = async () => {
    setError(null);
    if (!draft.code || !draft.name) { setError("Заполните код и название"); return; }
    try {
      setSaving(true);
      if (editId) await APIAdminTraitUpdate(adminToken, editId, draft);
      else await APIAdminTraitCreate(adminToken, draft);
      setOpen(false);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || "Не удалось сохранить");
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Удалить параметр «${t.name}»?`)) return;
    try { await APIAdminTraitDelete(adminToken, t.id); setItems((p) => p.filter((x) => x.id !== t.id)); }
    catch (e) { /* ignore */ }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography level="h3">Параметры</Typography>
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            Характеристики, которые измеряют тесты и по которым считается совместимость.
          </Typography>
        </Box>
        {canEdit && <Button startDecorator={<AddRoundedIcon />} onClick={openCreate}>Новый параметр</Button>}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Typography sx={{ py: 6, textAlign: "center", color: "text.tertiary" }}>Параметров пока нет</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table hoverRow sx={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Код</th>
                <th>Название</th>
                <th>Тип</th>
                <th>Подбор</th>
                <th style={{ textAlign: "center" }}>Шкала</th>
                <th style={{ textAlign: "center" }}>Вес</th>
                <th style={{ textAlign: "center" }}>Флаги</th>
                <th style={{ textAlign: "right" }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id}>
                  <td><Typography level="body-xs" sx={{ fontFamily: "monospace" }}>{t.code}</Typography></td>
                  <td><Typography level="body-sm" sx={{ fontWeight: 700 }}>{t.name}</Typography></td>
                  <td><Typography level="body-xs">{t.value_type === "scale" ? "шкала" : "категория"}</Typography></td>
                  <td><Typography level="body-xs">{t.match_strategy}</Typography></td>
                  <td style={{ textAlign: "center" }}>{t.value_type === "scale" ? `${t.scale_min}–${t.scale_max}` : "—"}</td>
                  <td style={{ textAlign: "center" }}>{t.weight}</td>
                  <td style={{ textAlign: "center" }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                      {t.is_public && <Chip size="sm" variant="soft" color="primary">публ.</Chip>}
                      {t.is_filterable && <Chip size="sm" variant="soft" color="success">фильтр</Chip>}
                      {!t.is_active && <Chip size="sm" variant="soft" color="neutral">выкл</Chip>}
                    </Stack>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {canEdit && (
                        <IconButton size="sm" variant="soft" color="primary" onClick={() => openEdit(t)}>
                          <EditRoundedIcon />
                        </IconButton>
                      )}
                      {isSuperadmin && (
                        <IconButton size="sm" variant="soft" color="danger" onClick={() => remove(t)}>
                          <DeleteRoundedIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      )}

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ width: { xs: "94%", sm: 520 }, borderRadius: "xl", maxHeight: "90vh", overflowY: "auto" }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 1 }}>{editId ? "Параметр" : "Новый параметр"}</Typography>
          <Stack spacing={1.5}>
            <FormControl>
              <FormLabel>Код (латиница)</FormLabel>
              <Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="spontaneity" disabled={!!editId} />
            </FormControl>
            <FormControl>
              <FormLabel>Название</FormLabel>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Спонтанность" />
            </FormControl>
            <FormControl>
              <FormLabel>Описание</FormLabel>
              <Textarea minRows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </FormControl>
            <Stack direction="row" spacing={1.5}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Тип значения</FormLabel>
                <Select value={draft.value_type} onChange={(_, v) => setDraft({ ...draft, value_type: v })}>
                  {VALUE_TYPES.map(([val, lbl]) => <Option key={val} value={val}>{lbl}</Option>)}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Стратегия подбора</FormLabel>
                <Select value={draft.match_strategy} onChange={(_, v) => setDraft({ ...draft, match_strategy: v })}>
                  {STRATEGIES.map(([val, lbl]) => <Option key={val} value={val}>{lbl}</Option>)}
                </Select>
              </FormControl>
            </Stack>
            {draft.value_type === "scale" && (
              <Stack direction="row" spacing={1.5}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Мин. шкалы</FormLabel>
                  <Input type="number" value={draft.scale_min} onChange={(e) => setDraft({ ...draft, scale_min: Number(e.target.value) })} />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Макс. шкалы</FormLabel>
                  <Input type="number" value={draft.scale_max} onChange={(e) => setDraft({ ...draft, scale_max: Number(e.target.value) })} />
                </FormControl>
              </Stack>
            )}
            <FormControl>
              <FormLabel>Вес в подборе</FormLabel>
              <Input type="number" value={draft.weight} onChange={(e) => setDraft({ ...draft, weight: Number(e.target.value) })} />
            </FormControl>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography level="body-sm">Доступен как фильтр</Typography>
              <Switch checked={draft.is_filterable} onChange={(e) => setDraft({ ...draft, is_filterable: e.target.checked })} />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography level="body-sm">Показывать в анкете</Typography>
              <Switch checked={draft.is_public} onChange={(e) => setDraft({ ...draft, is_public: e.target.checked })} />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography level="body-sm">Активен</Typography>
              <Switch checked={draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
            </Stack>
            {error && <Typography level="body-sm" color="danger">{error}</Typography>}
            <Button loading={saving} onClick={save}>Сохранить</Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}